import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// Create event registration with attendee information
export const createEventRegistration = async (registrationData) => {
  try {
    const registrationRef = await addDoc(collection(db, 'eventRegistrations'), {
      ...registrationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Also add to the event's attendees array for quick access
    await updateEventAttendeesList(registrationData.eventId, registrationData);
    
    return registrationRef.id;
  } catch (error) {
    console.error('Error creating event registration:', error);
    throw error;
  }
};

// Update event's attendees list
const updateEventAttendeesList = async (eventId, attendeeData) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (eventDoc.exists()) {
      const eventData = eventDoc.data();
      const currentAttendees = eventData.attendees || [];
      
      // Add new attendee to the list
      const newAttendee = {
        userId: attendeeData.userId,
        firstName: attendeeData.firstName,
        lastName: attendeeData.lastName,
        email: attendeeData.email,
        company: attendeeData.company,
        title: attendeeData.title,
        phone: attendeeData.phone,
        registrationType: attendeeData.registrationType,
        paymentStatus: attendeeData.paymentStatus,
        registrationDate: attendeeData.createdAt || new Date(),
        isMember: attendeeData.isMember || false,
        membershipTier: attendeeData.membershipTier || null,
        specialRequirements: attendeeData.specialRequirements || '',
        emergencyContact: attendeeData.emergencyContact || '',
        dietaryRestrictions: attendeeData.dietaryRestrictions || ''
      };
      
      await updateDoc(eventRef, {
        attendees: [...currentAttendees, newAttendee],
        attendeeCount: currentAttendees.length + 1,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating event attendees list:', error);
    throw error;
  }
};

// Get all registrations for an event
export const getEventRegistrations = async (eventId) => {
  try {
    const registrationsQuery = query(
      collection(db, 'eventRegistrations'),
      where('eventId', '==', eventId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(registrationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting event registrations:', error);
    throw error;
  }
};

// Get attendee statistics for an event
export const getEventAttendeeStats = async (eventId) => {
  try {
    const registrations = await getEventRegistrations(eventId);
    
    const stats = {
      total: registrations.length,
      members: registrations.filter(r => r.isMember).length,
      nonMembers: registrations.filter(r => !r.isMember).length,
      sponsors: registrations.filter(r => r.registrationType === 'sponsor').length,
      paid: registrations.filter(r => r.paymentStatus === 'paid').length,
      pending: registrations.filter(r => r.paymentStatus === 'pending').length,
      byTier: {}
    };
    
    // Count by membership tier
    registrations.forEach(registration => {
      if (registration.membershipTier) {
        stats.byTier[registration.membershipTier] = (stats.byTier[registration.membershipTier] || 0) + 1;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting attendee stats:', error);
    throw error;
  }
};

// Export attendees to CSV format
export const exportAttendeesToCSV = async (eventId, includePaymentInfo = false) => {
  try {
    const registrations = await getEventRegistrations(eventId);
    
    // Define CSV headers
    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Company',
      'Title',
      'Member Status',
      'Membership Tier',
      'Registration Type',
      'Registration Date',
      'Special Requirements',
      'Emergency Contact',
      'Dietary Restrictions'
    ];
    
    if (includePaymentInfo) {
      headers.push('Payment Status', 'Payment Method', 'Amount Paid');
    }
    
    // Convert registrations to CSV rows
    const rows = registrations.map(registration => {
      const row = [
        registration.firstName || '',
        registration.lastName || '',
        registration.email || '',
        registration.phone || '',
        registration.company || '',
        registration.title || '',
        registration.isMember ? 'Member' : 'Non-Member',
        registration.membershipTier || '',
        registration.registrationType || '',
        registration.createdAt ? new Date(registration.createdAt.seconds * 1000).toLocaleString() : '',
        registration.specialRequirements || '',
        registration.emergencyContact || '',
        registration.dietaryRestrictions || ''
      ];
      
      if (includePaymentInfo) {
        row.push(
          registration.paymentStatus || '',
          registration.paymentMethod || '',
          registration.amount ? `$${registration.amount}` : ''
        );
      }
      
      return row;
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Error exporting attendees to CSV:', error);
    throw error;
  }
};

// Enhanced Excel export with comprehensive attendee data
export const exportAttendeesToExcel = async (eventId, includePaymentInfo = false) => {
  try {
    const XLSX = await import('xlsx');
    const registrations = await getEventRegistrations(eventId);

    // Get event details for the filename
    const eventDoc = await getDoc(doc(db, 'events', eventId));
    const eventData = eventDoc.exists() ? eventDoc.data() : null;
    const eventTitle = eventData?.title || 'Event';

    // Enhanced data structure for comprehensive export
    const attendeeData = registrations.map((registration, index) => {
      const baseData = {
        'Registration #': index + 1,
        'Full Name': `${registration.firstName || ''} ${registration.lastName || ''}`.trim(),
        'Email': registration.email || '',
        'Phone': registration.phone || '',
        'Company': registration.company || '',
        'Job Title': registration.jobTitle || '',
        'Registration Type': registration.registrationType || '',
        'Registration Date': registration.createdAt ? new Date(registration.createdAt.seconds * 1000).toLocaleDateString() : '',
        'Event Date': eventData?.startDate ? new Date(eventData.startDate.seconds * 1000).toLocaleDateString() : '',
        'Dietary Restrictions': registration.dietaryRestrictions || '',
        'Accessibility Needs': registration.accessibilityNeeds || '',
        'Company Size': registration.companySize || '',
        'How Did You Hear About Us': registration.referralSource || '',
        'Special Requests': registration.specialRequests || '',
        'Networking Interests': registration.networkingInterests || '',
        'Status': registration.status || 'Registered'
      };

      // Add payment information if requested and available
      if (includePaymentInfo && registration.payment) {
        baseData['Payment Status'] = registration.payment.status || '';
        baseData['Payment Method'] = registration.payment.method || '';
        baseData['Amount Paid'] = registration.payment.amount || '';
        baseData['Transaction ID'] = registration.payment.transactionId || '';
        baseData['Payment Date'] = registration.payment.paidAt ? new Date(registration.payment.paidAt.seconds * 1000).toLocaleDateString() : '';
        baseData['Discount Applied'] = registration.payment.discountCode || '';
        baseData['Refund Status'] = registration.payment.refundStatus || '';
      }

      return baseData;
    });

    // Create workbook with multiple sheets
    const workbook = XLSX.utils.book_new();

    // Main attendee sheet
    const attendeeSheet = XLSX.utils.json_to_sheet(attendeeData);
    XLSX.utils.book_append_sheet(workbook, attendeeSheet, 'Attendees');

    // Summary sheet with event statistics
    const summaryData = [
      { Metric: 'Total Registrations', Value: registrations.length },
      { Metric: 'Event Title', Value: eventTitle },
      { Metric: 'Event Date', Value: eventData?.startDate ? new Date(eventData.startDate.seconds * 1000).toLocaleDateString() : 'TBD' },
      { Metric: 'Event Location', Value: eventData?.location || 'TBD' },
      { Metric: 'Virtual Event', Value: eventData?.isVirtual ? 'Yes' : 'No' },
      { Metric: 'Export Date', Value: new Date().toLocaleDateString() },
      { Metric: 'Export Time', Value: new Date().toLocaleTimeString() },
      { Metric: '', Value: '' },
      { Metric: 'Registration Breakdown:', Value: '' }
    ];

    // Add registration type breakdown
    const registrationTypes = {};
    registrations.forEach(reg => {
      const type = reg.registrationType || 'Standard';
      registrationTypes[type] = (registrationTypes[type] || 0) + 1;
    });

    Object.entries(registrationTypes).forEach(([type, count]) => {
      summaryData.push({ Metric: `${type} Registrations`, Value: count });
    });

    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Payment summary sheet (if payment info included)
    if (includePaymentInfo) {
      const paymentSummary = registrations
        .filter(reg => reg.payment)
        .map(reg => ({
          'Name': `${reg.firstName || ''} ${reg.lastName || ''}`.trim(),
          'Email': reg.email,
          'Payment Status': reg.payment.status || '',
          'Amount': reg.payment.amount || '',
          'Method': reg.payment.method || '',
          'Transaction ID': reg.payment.transactionId || '',
          'Date': reg.payment.paidAt ? new Date(reg.payment.paidAt.seconds * 1000).toLocaleDateString() : ''
        }));

      const paymentSheet = XLSX.utils.json_to_sheet(paymentSummary);
      XLSX.utils.book_append_sheet(workbook, paymentSheet, 'Payments');
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}_Attendees_${timestamp}.xlsx`;

    // Write file
    XLSX.writeFile(workbook, filename);

    return {
      success: true,
      filename,
      attendeeCount: registrations.length,
      exportDate: new Date().toISOString(),
      sheets: includePaymentInfo ? ['Attendees', 'Summary', 'Payments'] : ['Attendees', 'Summary']
    };


  } catch (error) {
    console.error('Error exporting attendees to Excel:', error);
    throw error;
  }
};

// Automatically update attendee list when payment is successful
export const updateAttendeeOnPaymentSuccess = async (eventId, userId, paymentData) => {
  try {
    // Find the registration for this user and event
    const registrationsQuery = query(
      collection(db, 'eventRegistrations'),
      where('eventId', '==', eventId),
      where('userId', '==', userId)
    );

    const registrationsSnapshot = await getDocs(registrationsQuery);

    if (registrationsSnapshot.empty) {
      throw new Error('Registration not found');
    }

    const registrationDoc = registrationsSnapshot.docs[0];
    const registrationRef = doc(db, 'eventRegistrations', registrationDoc.id);

    // Update registration with payment information
    await updateDoc(registrationRef, {
      paymentStatus: 'completed',
      paymentMethod: paymentData.paymentMethod,
      transactionId: paymentData.transactionId,
      amountPaid: paymentData.amount,
      paymentDate: paymentData.timestamp || new Date(),
      updatedAt: new Date()
    });

    return {
      success: true,
      registrationId: registrationDoc.id,
      message: 'Attendee list updated successfully'
    };
  } catch (error) {
    console.error('Error updating attendee on payment success:', error);
    throw error;
  }
};

// Get real-time attendee count for an event
export const getAttendeeCount = async (eventId) => {
  try {
    const registrationsQuery = query(
      collection(db, 'eventRegistrations'),
      where('eventId', '==', eventId),
      where('paymentStatus', '==', 'completed')
    );

    const registrationsSnapshot = await getDocs(registrationsQuery);
    return registrationsSnapshot.size;
  } catch (error) {
    console.error('Error getting attendee count:', error);
    return 0;
  }
};

// Get attendee statistics for an event
export const getAttendeeStats = async (eventId) => {
  try {
    const registrations = await getEventRegistrations(eventId);

    const stats = {
      total: registrations.length,
      byType: {},
      byMembershipTier: {},
      byPaymentStatus: {},
      totalRevenue: 0
    };

    registrations.forEach(registration => {
      // Count by registration type
      const type = registration.registrationType || 'standard';
      stats.byType[type] = (stats.byType[type] || 0) + 1;

      // Count by membership tier
      const tier = registration.membershipTier || 'non-member';
      stats.byMembershipTier[tier] = (stats.byMembershipTier[tier] || 0) + 1;

      // Count by payment status
      const status = registration.paymentStatus || 'pending';
      stats.byPaymentStatus[status] = (stats.byPaymentStatus[status] || 0) + 1;

      // Calculate revenue
      if (registration.paymentStatus === 'completed' && registration.amountPaid) {
        stats.totalRevenue += parseFloat(registration.amountPaid);
      }
    });

    return stats;
  } catch (error) {
    console.error('Error getting attendee stats:', error);
    return {
      total: 0,
      byType: {},
      byMembershipTier: {},
      byPaymentStatus: {},
      totalRevenue: 0
    };
  }
};

// Send automated email notifications (placeholder for future implementation)
export const sendAttendeeNotification = async (eventId, attendeeId, notificationType) => {
  try {
    // This would integrate with an email service like SendGrid, Mailgun, etc.
    // For now, we'll just log the notification
    console.log(`Sending ${notificationType} notification to attendee ${attendeeId} for event ${eventId}`);

    // In a real implementation, this would:
    // 1. Get attendee email from registration
    // 2. Get event details
    // 3. Generate appropriate email template
    // 4. Send via email service
    // 5. Log the notification in the database

    return {
      success: true,
      message: 'Notification queued for sending'
    };
  } catch (error) {
    console.error('Error sending attendee notification:', error);
    throw error;
  }
};

// Update registration status
export const updateRegistrationStatus = async (registrationId, updates) => {
  try {
    const registrationRef = doc(db, 'eventRegistrations', registrationId);
    await updateDoc(registrationRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating registration status:', error);
    throw error;
  }
};

// Delete registration
export const deleteRegistration = async (registrationId, eventId) => {
  try {
    // Delete from registrations collection
    await deleteDoc(doc(db, 'eventRegistrations', registrationId));
    
    // Remove from event's attendees list
    const eventRef = doc(db, 'events', eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (eventDoc.exists()) {
      const eventData = eventDoc.data();
      const updatedAttendees = eventData.attendees.filter(
        attendee => attendee.registrationId !== registrationId
      );
      
      await updateDoc(eventRef, {
        attendees: updatedAttendees,
        attendeeCount: updatedAttendees.length,
        updatedAt: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting registration:', error);
    throw error;
  }
};
